'use client';

import {Popover, PopoverButton, PopoverPanel} from '@headlessui/react';
import {useTranslations} from 'next-intl';
import React, {ChangeEvent, useEffect, useState} from 'react';
import SearchIcon from '@website/components/shared/svg/SearchIcon';
import {GetConditionsResponse} from '@website/types/art.types';
import {CloseIcon} from '@website/components/Gallery/CloseIcon';
import {FilterIcon} from './FilterIcon';

type SearchableConditionsSelectProps = {
  baseUrl: string;
  onFormSubmit?: (searchTerm: string, conditions: string[]) => void;
};

export const SearchForm: React.FC<SearchableConditionsSelectProps> = ({
  baseUrl,
  onFormSubmit,
}) => {
  const t = useTranslations('Gallery');
  const [selectedConditions, setSelectedConditions] = useState<
    {label: string; value: string}[]
  >([]);
  const [appliedConditions, setAppliedConditions] = useState<
    {label: string; value: string}[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [conditions, setConditions] = useState<
    {label: string; value: string}[]
  >([]);

  const fetchConditions = async () => {
    const res = await fetch(
      `${baseUrl}/api/wellnite/get-magic-art-condition-tags`,
    );
    const data: GetConditionsResponse = await res.json();
    return data.message;
  };

  useEffect(() => {
    fetchConditions().then(conditions => {
      setConditions(
        conditions.map(condition => ({
          label: condition.name,
          value: condition._id,
        })),
      );
    });
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleCheckboxChange = (
    e: ChangeEvent<HTMLInputElement>,
    selection: {label: string; value: string},
  ) => {
    const {checked} = e.target;

    if (checked) {
      setSelectedConditions(prev => [...prev, selection]);
    } else {
      setSelectedConditions(prev =>
        prev.filter(cond => cond.value !== selection.value),
      );
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // todo - maybe we should pass the id here
    onFormSubmit?.(
      searchTerm,
      appliedConditions.map(condition => condition.label),
    );
  };

  const handleApply = () => {
    setAppliedConditions(selectedConditions);
    onFormSubmit?.(
      searchTerm,
      selectedConditions.map(condition => condition.label),
    );
  };

  const removeCondition = (condition: {label: string; value: string}) => {
    setAppliedConditions(prev => {
      const newConditions = prev.filter(cond => cond.value !== condition.value);
      onFormSubmit?.(
        searchTerm,
        newConditions.map(condition => condition.label),
      );
      return newConditions;
    });
  };

  return (
    <Popover className="w-full pb-5">
      {({open}) => {
        useEffect(() => {
          setSelectedConditions(appliedConditions);
        }, [open]);

        return (
          <>
            <form className={'flex gap-2'} onSubmit={handleSubmit}>
              <SearchIcon className={'absolute mt-4 ml-5'} />
              <input
                type="text"
                placeholder={t('search')}
                className="bg-gray-200 focus:border-0 focus:border-none focus:outline-none w-full rounded-full  p-4 pl-14"
                onChange={e => handleChange(e)}
              />
              <PopoverButton className="flex justify-around items-center gap-2 px-4 py-2 border truncate bg-black text-white rounded-full w-[130px]">
                <p className="hidden sm:block">{t('filter')}:</p>
                <FilterIcon strokeColor={'text-white'} />
              </PopoverButton>
              <PopoverPanel
                anchor="bottom"
                className="flex flex-col shadow-lg border border-gray-100 relative rounded-[30px] bg-white py-4 px-2  overflow-hidden mt-1"
              >
                <div className="w-full sticky top-1 flex-col gap-3 rounded-md flex max-w-[350px] justify-start items-start">
                  <PopoverButton
                    onClick={() => {}}
                    className="flex ml-auto mr-2 fs justify-center items-center hover:bg-black h-6 w-6 group bg-transparent cursor-pointer rounded-full"
                  >
                    <CloseIcon
                      strokeColor="text-gray-700 group-hover:text-white"
                      strokeWidth={40}
                      classes="w-8 h-8"
                    />
                  </PopoverButton>
                  <p className={'p-2'}>{t('selectFilters')}:</p>
                </div>
                <div
                  className={
                    'h-[278px] w-full max-w-[350px] overflow-y-auto custom-scrollbar'
                  }
                >
                  {conditions?.map(condition => (
                    <label
                      className="flex border flex-row justify-start items-center gap-2 hover:bg-blue-50 p-3 m-2 rounded-xl cursor-pointer mt-4"
                      key={condition.value}
                    >
                      <input
                        type="checkbox"
                        checked={selectedConditions.some(
                          cond => cond.value === condition.value,
                        )}
                        value={condition.value}
                        onChange={event =>
                          handleCheckboxChange(event, condition)
                        }
                      />
                      <p className={"capitalize"}>{condition.label}</p>
                    </label>
                  ))}
                </div>
                <div className={'flex gap-2'}>
                  <PopoverButton
                    type="button"
                    className="w-full bg-white text-black border rounded-full p-2 mt-2"
                  >
                    {t('cancel')}
                  </PopoverButton>
                  <PopoverButton
                    type="submit"
                    onClick={() => handleApply()}
                    className="w-full bg-primary text-white rounded-full p-2 mt-2"
                  >
                    {t('apply')}
                  </PopoverButton>
                </div>
              </PopoverPanel>
            </form>
            {appliedConditions.length ? (
              <div className="flex items-left mt-5 flex-wrap  gap-2">
                {appliedConditions.map(tag => (
                  <span
                    key={tag.value}
                    className="flex text-black text-xs items-center gap-2 float-left bg-[#F4FEDE] rounded-full px-4 py-2 mr-2"
                  >
                    {tag.label}
                    <button
                      onClick={() => removeCondition(tag)}
                      className="flex justify-center items-center hover:bg-black h-6 w-6 group bg-transparent cursor-pointer rounded-full"
                    >
                      <CloseIcon
                        strokeColor="text-gray-700 group-hover:text-white"
                        strokeWidth={40}
                        classes="w-8 h-8"
                      />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
          </>
        );
      }}
    </Popover>
  );
};
